import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LoadingSkeleton from "../LoadingSkeleton";
import MagnifiableImage from "../Dashboard/TransactionDetail/TransactionImageCarousel/MagnifiableImage";
import { convertDateTimeToString } from "common/format";

export interface CarouselSlide {
  /** Label overlaid on the slide, e.g. "Park Start" */
  title: string;
  /** When provided, appended to the label as ": MM/DD/YYYY HH:MM AM/PM" */
  date?: Date;
  /** Image URL */
  imageSrc: string;
}

const IMAGE_STYLE: React.CSSProperties = { width: "100%", height: "100%", objectFit: "contain" };

/** Value get_transaction_image_urls returns in place of a URL once the image has expired */
export const EXPIRED_IMAGE_URL = "expired";

export const UNAVAILABLE_IMAGE_MESSAGE =
  "This image could not be found. It may have been removed according to the jurisdiction's retention policies.";

export interface SlideItemProps {
  slide: CarouselSlide;
  magnifyingGlass: boolean;
  onClick?: () => void;
}

export function SlideItem({ slide, magnifyingGlass, onClick }: SlideItemProps): JSX.Element {
  const isExpired = slide.imageSrc === EXPIRED_IMAGE_URL;
  const [loading, setLoading] = useState(!isExpired);
  const [loadFailed, setLoadFailed] = useState(false);
  const isUnavailable = isExpired || loadFailed;
  const handleClick = isUnavailable ? undefined : onClick;
  const label = slide.date ? `${slide.title}: ${convertDateTimeToString(slide.date)}` : slide.title;

  const handleError = () => {
    setLoadFailed(true);
    setLoading(false);
  };

  return (
    <LoadingSkeleton loading={loading} width="100%" height="100%">
      <Box
        onClick={handleClick}
        sx={{ width: "100%", height: "100%", position: "relative", cursor: handleClick ? "pointer" : "default" }}
      >
        {isUnavailable ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background: "linear-gradient(180deg, #e0e0e0 0%, #bdbdbd 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              // Keeps the message from running under the carousel's prev/next buttons
              px: 8,
            }}
          >
            <Typography color="text.primary" variant="body2" textAlign="center">
              {UNAVAILABLE_IMAGE_MESSAGE}
            </Typography>
          </Box>
        ) : magnifyingGlass ? (
          <MagnifiableImage
            imageSrc={slide.imageSrc}
            style={IMAGE_STYLE}
            onLoad={() => setLoading(false)}
            onError={handleError}
          />
        ) : (
          <img
            src={slide.imageSrc}
            alt={slide.title}
            style={{ ...IMAGE_STYLE, display: "block" }}
            onLoad={() => setLoading(false)}
            onError={handleError}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(0deg, rgba(0, 0, 0, 0.00) 10.18%, rgba(0, 0, 0, 0.50) 100%)",
            pointerEvents: "none",
          }}
        />
        <Box sx={{ position: "absolute", top: [8, 8, 8, 24], left: [14, 14, 14, 16], pointerEvents: "none" }}>
          <Typography color="white" fontSize={["1rem", "1rem", "1rem", "1.75rem"]} fontWeight={[600, 600, 600, 400]}>
            {label}
          </Typography>
        </Box>
      </Box>
    </LoadingSkeleton>
  );
}

export default SlideItem;
